/**
 * WebSocket Encryption
 * Handles payload encryption/decryption for WebSocket sync.
 * Extracted from websocket.js for better separation of concerns.
 *
 * Uses AES-GCM with a 12-byte random IV prepended to ciphertext,
 * then base64-encoded for transmission.
 */

import { encodeBytes, decodeBytes } from "./ws-protocol.js";
import { compress, decompress } from "./compression.js";

/**
 * Encrypt a payload for transmission
 * @param {Uint8Array} data - The data to encrypt
 * @param {CryptoKey} key - AES-GCM encryption key
 * @param {string} [docId] - Optional document ID for AAD binding
 * @returns {Promise<string>} Base64 encoded encrypted data (IV + ciphertext)
 */
export async function encryptPayload(data, key, docId) {
  // Verify encryption key is a CryptoKey object
  if (!(key instanceof CryptoKey)) {
    throw new Error("Encryption key is not a valid CryptoKey object");
  }

  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(12));

  // M-14: Use Additional Authenticated Data (AAD) when docId is provided.
  // This binds the ciphertext to the document, preventing cross-document replay.
  const aad = docId ? new TextEncoder().encode(docId) : undefined;

  // Compress before encryption (encrypted data is incompressible)
  const compressed = await compress(data);

  // Encrypt with AES-GCM
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv, ...(aad && { additionalData: aad }) },
    key,
    compressed,
  );

  // Combine IV + ciphertext
  const combined = new Uint8Array(iv.length + encrypted.byteLength);
  combined.set(iv);
  combined.set(new Uint8Array(encrypted), iv.length);

  return encodeBytes(combined);
}

/**
 * Decrypt a payload from the server
 * @param {string} base64Data - Base64 encoded encrypted data
 * @param {CryptoKey} key - AES-GCM encryption key
 * @param {string} [docId] - Optional document ID for AAD verification
 * @returns {Promise<Uint8Array>} Decrypted data
 */
export async function decryptPayload(base64Data, key, docId) {
  const data = decodeBytes(base64Data);

  // Verify encryption key is a CryptoKey object
  if (!(key instanceof CryptoKey)) {
    throw new Error("Encryption key is not a valid CryptoKey object");
  }

  // Split IV and ciphertext
  const iv = data.slice(0, 12);
  const ciphertext = data.slice(12);

  // M-14: Try decryption with AAD first (new format), then without (legacy format).
  // Existing data encrypted before this change has no AAD. The fallback ensures
  // backward compatibility during the transition period.
  // TODO: REMOVE-AFTER-MIGRATION — Remove no-AAD fallback once all baselines have been
  // re-encrypted with AAD (after a full compaction cycle). The fallback weakens
  // cross-document replay protection in theory (requires same DEK across docs).
  if (docId) {
    try {
      const aad = new TextEncoder().encode(docId);
      const decrypted = await crypto.subtle.decrypt(
        { name: "AES-GCM", iv, additionalData: aad },
        key,
        ciphertext,
      );
      return await decompress(new Uint8Array(decrypted));
    } catch (aadErr) {
      // Fall through to legacy (no-AAD) decryption
      // Log at debug level to aid troubleshooting without noise
      console.debug("[WS Encryption] AAD decryption failed, trying legacy (no-AAD):", aadErr?.message);
    }
  }

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: iv },
    key,
    ciphertext,
  );

  return await decompress(new Uint8Array(decrypted));
}
